import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Eye, EyeOff, Check, X, Mail, Lock, User, Building2, Shield, ArrowLeft } from 'lucide-react';

const GOOGLE_ICON = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);
import { authApi, getDashboardPathForUser } from '../lib/api';

const AuthPages = () => {
  const location = useLocation();
  const pathname = (location?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '')).replace(/\/$/, '') || '/';
  const pathLower = pathname.toLowerCase();
  const isUsPartnersAuth = pathLower.startsWith('/partners');
  const isSignUpPage = pathname.includes('signup');
  const isFRPage = pathname.startsWith('/fr');
  const returnTo = pathname.startsWith('/fr/partenaires') ? '/fr/partenaires' : pathname.startsWith('/fr') ? '/fr' : null;
  const searchParams = new URLSearchParams(location?.search || (typeof window !== 'undefined' ? window.location.search : ''));
  const targetSection = searchParams.get('section');
  const baseDashboardPathFromRoute = getDashboardPathForUser(null, pathname);

  /** Partenaires US : tableau de bord sans query `section` (URL canonique `/partners/dashboard`). */
  const dashboardPathAfterAuth =
    isUsPartnersAuth
      ? baseDashboardPathFromRoute
      : targetSection
        ? `${baseDashboardPathFromRoute}?section=${encodeURIComponent(targetSection)}`
        : baseDashboardPathFromRoute;
  const googleReturnTo = (() => {
    if (isFRPage) {
      return pathname.startsWith('/fr/partenaires')
        ? '/fr/partenaires/onboarding/plan'
        : '/fr/onboarding/plan';
    }
    return isUsPartnersAuth ? '/partners/onboarding/plan' : '/onboarding/plan';
  })();
  const [isSignUp, setIsSignUp] = useState(isSignUpPage);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    companyName: '',
    agreedToTerms: false,
    rememberMe: false,
  });

  const [passwordStrength, setPasswordStrength] = useState(0);
  const [emailValid, setEmailValid] = useState(null);

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]+/)) strength++;
    if (password.match(/[A-Z]+/)) strength++;
    if (password.match(/[0-9]+/)) strength++;
    if (password.match(/[$@#&!]+/)) strength++;
    setPasswordStrength(strength);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailValid(re.test(email));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'password') checkPasswordStrength(value);
    if (name === 'email') validateEmail(value);
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    if (isSignUp && !formData.agreedToTerms) {
      setError(isFRPage ? 'Veuillez accepter les conditions générales et la politique de confidentialité' : 'Please agree to the Terms of Service and Privacy Policy');
      setLoading(false);
      return;
    }
    if (!formData.email || !formData.password) {
      setError(isFRPage ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill in all required fields');
      setLoading(false);
      return;
    }
    if (isSignUp && !formData.fullName) {
      setError(isFRPage ? 'Veuillez entrer votre nom complet' : 'Please enter your full name');
      setLoading(false);
      return;
    }

    try {
      let loggedUser;
      const isPartnerAuthPath = pathLower.startsWith('/partners') || pathname.startsWith('/fr/partenaires');
      if (isSignUp) {
        const isPartner = pathname.startsWith('/fr/partenaires') || pathLower.startsWith('/partners');
        loggedUser = await authApi.signup(
          formData.fullName,
          formData.email,
          formData.password,
          formData.companyName || undefined,
          isPartner ? 'partner' : 'agent'
        );
      } else {
        loggedUser = await authApi.login(formData.email, formData.password, { partnerAuth: isPartnerAuthPath });
      }
      let redirectUrl;
      if (isFRPage) {
        redirectUrl = isPartnerAuthPath
          ? '/fr/partenaires/onboarding/plan'
          : '/fr/onboarding/plan';
      } else {
        redirectUrl = isPartnerAuthPath
          ? '/partners/onboarding/plan'
          : '/onboarding/plan';
      }
      window.location.href = redirectUrl;
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('déjà utilisé') || msg.includes('already registered')) {
        setError(
          isFRPage
            ? 'Cet email a déjà un compte. Connectez-vous.'
            : 'This email already has an account. Please sign in instead.',
        );
        // Bascule auto sur l'onglet Sign In pour aider l'utilisateur.
        setIsSignUp(false);
      } else if (msg.includes('incorrect') || msg.includes('Invalid credentials')) {
        setError(isFRPage ? 'Email ou mot de passe incorrect' : 'Incorrect email or password');
      } else {
        setError(msg || (isFRPage ? 'Une erreur est survenue. Veuillez réessayer.' : 'An error occurred. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 2) return 'bg-red-500';
    if (passwordStrength <= 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (isFRPage) {
      if (passwordStrength <= 2) return 'Faible';
      if (passwordStrength <= 3) return 'Moyen';
      return 'Fort';
    }
    if (passwordStrength <= 2) return 'Weak';
    if (passwordStrength <= 3) return 'Medium';
    return 'Strong';
  };

  const partnersLandingUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/partners` : 'https://3dtovideo.com/partners';
  const backHref = (isFRPage && returnTo) ? returnTo : (isUsPartnersAuth ? partnersLandingUrl : '/');
  const backLabelEn = 'Back to home';

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#3C59B2]/10 flex items-center justify-center p-4">
      <div className="font-manrope w-full max-w-6xl grid lg:grid-cols-2 gap-0 bg-white border border-[#E2E8F0] rounded-3xl overflow-hidden card-hover">

        <div className="bg-[#3C59B2] p-12 text-white flex flex-col justify-between hidden lg:flex">
          <div>
            <a href={backHref} className="flex items-center space-x-2 mb-12 cursor-pointer hover:opacity-80 transition-opacity inline-block">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm">{isFRPage ? 'Retour à l\'accueil' : backLabelEn}</span>
            </a>

            <h2 className="font-manrope text-4xl font-bold mb-6">
              {isFRPage
                ? (isSignUp ? 'Créez votre compte' : 'Content de vous revoir')
                : (isSignUp ? 'Start Converting Today' : 'Welcome Back')}
            </h2>
            <p className="text-blue-100 text-lg mb-12">
              {isFRPage
                ? (isSignUp
                  ? 'Transformez vos visites Matterport en vidéos professionnelles. Inscrivez-vous en 2 minutes.'
                  : 'Connectez-vous pour accéder à votre espace et poursuivez.')
                : (isSignUp
                  ? 'Turn your Matterport tours into professional walkthrough videos.'
                  : 'Sign in to access your dashboard and continue creating amazing property videos.')}
            </p>

            {isSignUp && !isFRPage ? null : null}
          </div>

          <div className="border-t border-white/20 pt-6" />
        </div>

        <div className="p-8 lg:p-12">
          <a href={backHref} className="lg:hidden flex items-center space-x-2 mb-6 text-gray-600 hover:text-gray-900 transition-colors inline-block">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">{isFRPage ? 'Retour à l\'accueil' : backLabelEn}</span>
          </a>

          <div className="flex gap-2 mb-8 p-1 rounded-full bg-white border-2 border-[#E2E8F0]">
            <button
              type="button"
              onClick={() => setIsSignUp(false)}
              disabled={loading}
              className={`btn-premium overflow-hidden flex-1 py-3 px-6 rounded-full font-semibold transition-all ${!isSignUp ? 'bg-[#3C59B2] text-white' : 'bg-transparent border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}
            >
              {isFRPage ? 'Connexion' : 'Sign In'}
            </button>
            <button
              type="button"
              onClick={() => setIsSignUp(true)}
              disabled={loading}
              className={`btn-premium overflow-hidden flex-1 py-3 px-6 rounded-full font-semibold transition-all ${isSignUp ? 'bg-[#3C59B2] text-white' : 'bg-transparent border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}
            >
              {isFRPage ? 'S\'inscrire' : 'Sign Up'}
            </button>
          </div>

          <div className="mb-8">
            <h1 className="font-manrope text-3xl font-bold text-gray-900 mb-2">
              {isFRPage ? (isSignUp ? 'Créer un compte' : 'Connexion') : (isSignUp ? 'Create Your Account' : 'Sign In')}
            </h1>
            <p className="text-gray-600">
              {isFRPage
                ? (isSignUp ? 'Inscrivez-vous en 2 minutes' : 'Accédez à votre espace et poursuivez.')
                : (isSignUp ? 'Start your Matterport-to-video order in 2 minutes' : 'Access your dashboard and continue creating')}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Google en premier */}
          <a
            href={`/api/auth/google?return_to=${encodeURIComponent(googleReturnTo)}&locale=${isFRPage ? 'fr' : 'en'}`}
            className="btn-premium overflow-hidden w-full py-3 rounded-full font-semibold border-2 border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-all flex items-center justify-center gap-2 mb-4"
          >
            {GOOGLE_ICON}
            <span>{isFRPage ? 'Continuer avec Google' : 'Continue with Google'}</span>
          </a>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-white text-gray-500">{isFRPage ? 'Ou remplir le formulaire' : 'Or use email/password'}</span>
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="space-y-4"
          >
            {isSignUp && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{isFRPage ? 'Nom complet *' : 'Full Name *'}</label>
                  <div className="relative">
                    <User className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none transition-colors"
                      placeholder={isFRPage ? 'Jean Dupont' : 'John Doe'}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {isFRPage ? 'Nom de l\'entreprise ' : 'Company Name '}<span className="text-gray-400 font-normal">({isFRPage ? 'optionnel' : 'optional'})</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none transition-colors"
                      placeholder={isFRPage ? 'Votre entreprise' : 'Your Photography Business'}
                      disabled={loading}
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{isFRPage ? 'Adresse email *' : 'Email Address *'}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-11 pr-11 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                    emailValid === null
                      ? 'border-gray-200 focus:border-[#3C59B2]'
                      : emailValid
                      ? 'border-green-500 focus:border-green-500'
                      : 'border-red-500 focus:border-red-500'
                  }`}
                  placeholder="you@company.com"
                  disabled={loading}
                />
                {emailValid !== null && (
                  <div className="absolute right-3 top-3.5">
                    {emailValid ? <Check className="w-5 h-5 text-green-500" /> : <X className="w-5 h-5 text-red-500" />}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{isFRPage ? 'Mot de passe *' : 'Password *'}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-11 pr-11 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none transition-colors"
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {isSignUp && formData.password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">{isFRPage ? 'Solidité du mot de passe\u00a0:' : 'Password strength:'}</span>
                    <span className={`text-xs font-semibold ${passwordStrength <= 2 ? 'text-red-500' : passwordStrength <= 3 ? 'text-yellow-500' : 'text-green-500'}`}>
                      {getPasswordStrengthText()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                      style={{ width: `${(passwordStrength / 5) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {!isSignUp && (
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded border-gray-300 text-[#3C59B2] focus:ring-[#3C59B2]"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-600">{isFRPage ? 'Se souvenir de moi' : 'Remember me'}</span>
                </label>
                <a
                  href={isFRPage ? (pathname.startsWith('/fr/partenaires') ? '/fr/partenaires/forgot-password' : '/fr/forgot-password') : '/forgot-password'}
                  className="text-sm text-[#3C59B2] hover:underline"
                >
                  {isFRPage ? 'Mot de passe oublié ?' : 'Forgot password?'}
                </a>
              </div>
            )}

            {isSignUp && (
              <label className="flex items-start space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="agreedToTerms"
                  checked={formData.agreedToTerms}
                  onChange={handleInputChange}
                  className="w-4 h-4 rounded border-gray-300 text-[#3C59B2] focus:ring-[#3C59B2] mt-1"
                  disabled={loading}
                />
                <span className="text-sm text-gray-600">
                  {isFRPage ? (
                    <>J'accepte les <a href="/terms" className="text-[#3C59B2] hover:underline">Conditions générales d'utilisation</a> et la <a href="/privacy" className="text-[#3C59B2] hover:underline">Politique de confidentialité</a>.</>
                  ) : (
                    <>I agree to the <a href="/terms" className="text-[#3C59B2] hover:underline">Terms of Service</a> and <a href="/privacy" className="text-[#3C59B2] hover:underline">Privacy Policy</a></>
                  )}
                </span>
              </label>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-premium overflow-hidden w-full bg-[#3C59B2] text-white py-3 rounded-full font-semibold hover:bg-[#2d4a8f] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (isFRPage ? 'Chargement...' : 'Loading...') : isFRPage ? (isSignUp ? 'Créer mon compte' : 'Se connecter') : (isSignUp ? 'Create Account' : 'Sign In')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuthPages;
